import {Injectable} from '@angular/core';
import {DateRange, LocationInterface, LocationsList, PresigneFileData, RowLocationsList} from '@core/models';
import {FileUploadHelper} from '@core/helpers';
import {BaseApiService} from '@core/api-services';
import moment from 'moment';
import {DateTypeFilterEnum} from '@core/enums';
import {UserService} from '@core/services/user.service';
import {LocationDateType, LocationEndDate, LocationStartDate} from '@constants';
import {CookieService} from 'ngx-cookie-service';
import {lastValueFrom} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class LocationsInteractionsService {

    constructor(
        private _baseApiService: BaseApiService,
        private _userService: UserService,
        private _cookieService: CookieService
    ) {
    }

    filterData(data: RowLocationsList[]): LocationsList[] {
        return data.map((location) => {
            return {
                image: location.location_info?.image,
                location_id: location.location_id,
                location_name: location.location_name,
                phone_number: location.phone_number,
                contact_name: location.contact_info?.contact_name ?? "",
                contact_email: location.contact_info?.contact_email ?? "",
                contact_phone_number: location.contact_info?.contact_phone_number,
                avg_players: Math.round((+location.player_count / +location.event_count) * 100) / 100,
                total_players: location.player_count,
                events_hosted: +location.event_count
            };
        });
    }

    async startPdfUpload(locationId: string, pdfFile: File): Promise<Pick<LocationInterface, 'location_info'>> {
        const fileDataForAWS: PresigneFileData = {
            keyName: `${locationId}.pdf`,
            contentType: 'application/pdf'
        };
        const fileDataForBase = {
            location_id: fileDataForAWS.keyName,
            pdf_name: pdfFile.name
        };

        try {
            const AWSRequestUrl = await lastValueFrom(this._baseApiService.locationsService.getPresignedUrl(fileDataForAWS));
            const base64File = await FileUploadHelper.FileToBase64(pdfFile);
            const blobFile = FileUploadHelper.base64ToBlob(base64File);

            await lastValueFrom(this._baseApiService.locationsService.uploadFileToAWS(AWSRequestUrl, blobFile));
            return await lastValueFrom(this._baseApiService.locationsService.uploadPdf(fileDataForBase));
        } catch (er) {
            return null;
        }
    }

    public setStartDate(value: DateTypeFilterEnum, defaultStartDate?: string | moment.Moment): string | moment.Moment {
        let startDate: moment.Moment | string = moment(defaultStartDate);
        if (value === DateTypeFilterEnum.all) {
            startDate = this._userService.user.created_at;
        }

        if (value === DateTypeFilterEnum.week) {
            startDate = moment().subtract(7, 'd').utc(false).startOf('day').format();
        }

        if (value === DateTypeFilterEnum.month) {
            const lastMonthDay = moment().subtract(1, 'months').endOf('month').daysInMonth();
            startDate = moment().subtract(lastMonthDay, 'd').utc(false).startOf('day').format();
        }

        if (value === DateTypeFilterEnum.currentMonth) {
            startDate = moment().utc(false).startOf('day').startOf('month').format();
        }

        return startDate;
    }

    public checkIsDropdownDate(date: DateRange): boolean {
        return !((date.end !== moment()) ||
            (
                date.start !== this.setStartDate(DateTypeFilterEnum.all) &&
                date.start !== this.setStartDate(DateTypeFilterEnum.month) &&
                date.start !== this.setStartDate(DateTypeFilterEnum.week) &&
                date.start !== this.setStartDate(DateTypeFilterEnum.currentMonth)
            ));

    }

    set setLocationDateCookie(data: { range: DateRange, type: DateTypeFilterEnum | '' }) {
        if (this._cookieService.get(LocationStartDate)) {
            this.cleanLocationDateCookie();
        }
        this._cookieService.set(LocationStartDate, moment(data.range.start).utc().format());
        this._cookieService.set(LocationEndDate, moment(data.range.end).utc().format());
        this._cookieService.set(LocationDateType, data.type);
    }

    get getLocationDateCookie() {
        return {
            range: {
                start: this._cookieService.get(LocationStartDate),
                end: this._cookieService.get(LocationEndDate),
            },
            type: this._cookieService.get(LocationDateType),
        };
    }

    public cleanLocationDateCookie() {
        this._cookieService.delete(LocationStartDate);
        this._cookieService.delete(LocationEndDate);
    }
}
